/**
 * Native Calendar Service
 * 
 * Provides TRUE native calendar synchronization using @capgo/capacitor-calendar plugin.
 * Creates, updates, and deletes events directly in the device's native calendar.
 * 
 * Features:
 * - Direct native calendar API access on iOS and Android
 * - Stores native event IDs for update/delete operations
 * - Prevents duplicate events
 * - Handles recurring events (yearly birthdays, etc.)
 * - Graceful permission handling
 * - ICS export as web-only fallback
 */

import { Capacitor } from '@capacitor/core';
import { toast } from 'sonner';

// Dynamic import for native calendar plugin (only available in native builds)
let CapacitorCalendar = null;

// Permission states
export const CALENDAR_PERMISSION = {
  GRANTED: 'granted',
  DENIED: 'denied',
  RESTRICTED: 'restricted',
  NOT_DETERMINED: 'notDetermined',
  WRITE_ONLY: 'writeOnly', // iOS 17+ partial access
};

// Sync status for records
export const SYNC_STATUS = {
  NOT_SYNCED: 'not_synced',
  SYNCED: 'synced',
  SYNC_FAILED: 'sync_failed',
  NEEDS_UPDATE: 'needs_update',
  INCOMPLETE_DATA: 'incomplete_data',
};

/**
 * Initialize the calendar plugin (lazy load)
 */
const initCalendarPlugin = async () => {
  if (CapacitorCalendar) return true;
  
  if (!Capacitor.isNativePlatform()) {
    console.log('Calendar plugin not available in web environment');
    return false;
  }
  
  try {
    const module = await import('@capgo/capacitor-calendar');
    CapacitorCalendar = module.CapacitorCalendar;
    return true;
  } catch (error) {
    console.error('Failed to load calendar plugin:', error);
    return false;
  }
};

/**
 * Check if native calendar is available
 */
export const isNativeCalendarAvailable = () => {
  return Capacitor.isNativePlatform();
};

/**
 * Get current calendar permission status
 */
export const getCalendarPermissionStatus = async () => {
  if (!await initCalendarPlugin()) {
    return { status: CALENDAR_PERMISSION.NOT_DETERMINED, isNative: false };
  }
  
  try {
    const result = await CapacitorCalendar.checkPermission();
    return {
      status: result.result || CALENDAR_PERMISSION.NOT_DETERMINED,
      isNative: true,
    };
  } catch (error) {
    console.error('Failed to check calendar permission:', error);
    return { status: CALENDAR_PERMISSION.NOT_DETERMINED, isNative: true };
  }
};

/**
 * Request full calendar access (iOS 17+ and Android)
 * Only call this when user initiates a calendar sync action
 */
export const requestCalendarPermission = async () => {
  if (!await initCalendarPlugin()) {
    return { granted: false, status: CALENDAR_PERMISSION.NOT_DETERMINED };
  }
  
  try {
    // Request full access for iOS 17+
    const result = await CapacitorCalendar.requestFullCalendarAccess();
    const granted = result.result === CALENDAR_PERMISSION.GRANTED || 
                    result.result === CALENDAR_PERMISSION.WRITE_ONLY;
    
    if (!granted) {
      toast.error('Calendar access is turned off', {
        description: 'Enable calendar access in Settings to sync your dates.',
        duration: 5000,
      });
    }
    
    return { granted, status: result.result };
  } catch (error) {
    console.error('Failed to request calendar permission:', error);
    
    // Fallback to legacy permission request for older iOS
    try {
      const legacyResult = await CapacitorCalendar.requestPermission();
      const granted = legacyResult.result === CALENDAR_PERMISSION.GRANTED;
      return { granted, status: legacyResult.result };
    } catch (legacyError) {
      console.error('Legacy permission request failed:', legacyError);
      return { granted: false, status: CALENDAR_PERMISSION.DENIED };
    }
  }
};

/**
 * Get list of available calendars on the device
 */
export const getAvailableCalendars = async () => {
  if (!await initCalendarPlugin()) {
    return [];
  }
  
  try {
    const { calendars } = await CapacitorCalendar.listCalendars();
    return calendars || [];
  } catch (error) {
    console.error('Failed to list calendars:', error);
    return [];
  }
};

/**
 * Get the default calendar for creating events
 */
export const getDefaultCalendar = async () => {
  const calendars = await getAvailableCalendars();
  
  // Find the default calendar (usually the primary one)
  const defaultCal = calendars.find(cal => cal.isDefault) || 
                     calendars.find(cal => cal.isPrimary) ||
                     calendars[0];
  
  return defaultCal || null;
};

/**
 * Create a native calendar event
 * Returns the native event ID for storage
 */
export const createNativeEvent = async (eventData, calendarId = null) => {
  if (!await initCalendarPlugin()) {
    return { success: false, error: 'Calendar plugin not available' };
  }
  
  const { granted } = await requestCalendarPermission();
  if (!granted) {
    return { success: false, error: 'Calendar permission denied' };
  }
  
  try {
    const {
      title,
      description = '',
      location = '',
      startDate,
      endDate,
      allDay = false,
      recurrence = null,
      reminder = null, // minutes before
    } = eventData;
    
    if (!title || !startDate) {
      return { success: false, error: 'Title and start date are required' };
    }
    
    // Convert dates to timestamps (milliseconds)
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date(start.getTime() + 60 * 60 * 1000); // Default 1 hour
    
    // Build event options
    const eventOptions = {
      title,
      startDate: start.getTime(),
      endDate: end.getTime(),
      isAllDay: allDay,
    };
    
    if (description) eventOptions.notes = description;
    if (location) eventOptions.location = location;
    if (calendarId) eventOptions.calendarId = calendarId;
    
    // Handle recurrence
    if (recurrence) {
      const recurrenceMap = {
        yearly: 'YEARLY',
        monthly: 'MONTHLY',
        weekly: 'WEEKLY',
        daily: 'DAILY',
      };
      if (recurrenceMap[recurrence]) {
        eventOptions.recurrence = {
          frequency: recurrenceMap[recurrence],
          interval: 1,
        };
      }
    }
    
    // Handle reminder/alarm
    if (reminder && reminder > 0) {
      eventOptions.alerts = [{ offset: -reminder }]; // Negative for before event
    }
    
    // Create the event
    const result = await CapacitorCalendar.createEvent(eventOptions);
    
    if (result && result.id) {
      return { 
        success: true, 
        eventId: result.id,
        message: 'Event added to calendar'
      };
    } else {
      return { success: false, error: 'Event created but no ID returned' };
    }
  } catch (error) {
    console.error('Failed to create calendar event:', error);
    return { success: false, error: error.message || 'Failed to create event' };
  }
};

/**
 * Update an existing native calendar event
 */
export const updateNativeEvent = async (eventId, eventData) => {
  if (!await initCalendarPlugin()) {
    return { success: false, error: 'Calendar plugin not available' };
  }
  
  if (!eventId) {
    return { success: false, error: 'No event ID provided' };
  }
  
  try {
    const {
      title,
      description,
      location,
      startDate,
      endDate,
      allDay,
    } = eventData;
    
    const updateOptions = { id: eventId };
    
    if (title) updateOptions.title = title;
    if (description !== undefined) updateOptions.notes = description;
    if (location !== undefined) updateOptions.location = location;
    if (startDate) updateOptions.startDate = new Date(startDate).getTime();
    if (endDate) updateOptions.endDate = new Date(endDate).getTime();
    if (allDay !== undefined) updateOptions.isAllDay = allDay;
    
    await CapacitorCalendar.modifyEvent(updateOptions);
    
    return { success: true, message: 'Calendar event updated' };
  } catch (error) {
    console.error('Failed to update calendar event:', error);
    return { success: false, error: error.message || 'Failed to update event' };
  }
};

/**
 * Delete a native calendar event
 */
export const deleteNativeEvent = async (eventId) => {
  if (!await initCalendarPlugin()) {
    return { success: false, error: 'Calendar plugin not available' };
  }
  
  if (!eventId) {
    return { success: false, error: 'No event ID provided' };
  }
  
  try {
    await CapacitorCalendar.deleteEvent({ id: eventId });
    return { success: true, message: 'Calendar event removed' };
  } catch (error) {
    console.error('Failed to delete calendar event:', error);
    return { success: false, error: error.message || 'Failed to delete event' };
  }
};

/**
 * Check if an event exists in the native calendar
 * Used for duplicate prevention
 */
export const checkEventExists = async (eventId) => {
  if (!await initCalendarPlugin()) {
    return false;
  }
  
  if (!eventId) return false;
  
  try {
    // Try to fetch the event - if it fails, it doesn't exist
    const result = await CapacitorCalendar.getEventById({ id: eventId });
    return !!result;
  } catch {
    return false;
  }
};

/**
 * Convert a Forget Me Not date record to calendar event format
 */
export const dateRecordToEventData = (dateRecord) => {
  const {
    title,
    name,
    description,
    notes,
    date,
    time,
    end_time,
    location,
    all_day,
    reminder_minutes,
    recurrence,
    repeat_frequency,
    type,
    person_names,
    // Additional fields for context
    gift_ideas,
    confirmation_number,
    provider_office,
  } = dateRecord;
  
  // Build title
  let eventTitle = title || name || 'Forget Me Not Event';
  if (type === 'birthday' && person_names?.length > 0) {
    eventTitle = `${person_names.join(' & ')}'s Birthday`;
  }
  
  // Build description
  let eventDescription = description || notes || '';
  if (gift_ideas) eventDescription += `\n\nGift Ideas: ${gift_ideas}`;
  if (confirmation_number) eventDescription += `\n\nConfirmation: ${confirmation_number}`;
  if (provider_office) eventDescription += `\n\n${provider_office}`;
  
  // Determine if this is a recurring annual event
  const isAnnualEvent = ['birthday', 'anniversary'].includes(type) || 
                        recurrence === 'yearly' || 
                        repeat_frequency === 'yearly';
  
  // Build start date with time
  let startDate = new Date(date);
  if (time && all_day !== true) {
    const [hours, minutes] = time.split(':');
    startDate.setHours(parseInt(hours, 10), parseInt(minutes, 10));
  }
  
  // Build end date
  let endDate = null;
  if (end_time && all_day !== true) {
    endDate = new Date(date);
    const [hours, minutes] = end_time.split(':');
    endDate.setHours(parseInt(hours, 10), parseInt(minutes, 10));
  }
  
  return {
    title: eventTitle,
    description: eventDescription.trim(),
    location: location || '',
    startDate,
    endDate,
    allDay: all_day !== false && !time,
    recurrence: isAnnualEvent ? 'yearly' : (recurrence || repeat_frequency || null),
    reminder: reminder_minutes || (type === 'birthday' ? 1440 : 60), // 1 day for birthdays, 1 hour otherwise
  };
};

/**
 * Sync a single date record to native calendar
 * Returns the native event ID to store with the record
 */
export const syncDateToCalendar = async (dateRecord, existingEventId = null) => {
  const eventData = dateRecordToEventData(dateRecord);
  
  // Check if already synced and event still exists
  if (existingEventId) {
    const exists = await checkEventExists(existingEventId);
    if (exists) {
      // Update existing event
      const result = await updateNativeEvent(existingEventId, eventData);
      return {
        ...result,
        eventId: existingEventId,
        action: 'updated',
      };
    }
  }
  
  // Create new event
  const result = await createNativeEvent(eventData);
  return {
    ...result,
    action: 'created',
  };
};

/**
 * Remove a synced event from calendar
 */
export const unsyncDateFromCalendar = async (eventId) => {
  if (!eventId) return { success: true, message: 'No event to remove' };
  return await deleteNativeEvent(eventId);
};

/**
 * Validate if a date record has enough data to sync
 */
export const canSyncDateRecord = (dateRecord) => {
  if (!dateRecord) return { canSync: false, reason: 'No record provided' };
  if (!dateRecord.title && !dateRecord.name && !dateRecord.person_names?.length) {
    return { canSync: false, reason: 'Missing title or name' };
  }
  if (!dateRecord.date) {
    return { canSync: false, reason: 'Missing date' };
  }
  return { canSync: true };
};

/**
 * ICS FALLBACK - Only for web or as manual export option
 * This is NOT automatic sync, just a downloadable file
 */
export const generateICSContent = (event) => {
  const {
    title,
    description = '',
    location = '',
    startDate,
    endDate,
    allDay = false,
    recurrence = null,
    reminder = null,
  } = event;

  const formatDate = (date, isAllDay) => {
    const d = new Date(date);
    if (isAllDay) {
      return d.toISOString().split('T')[0].replace(/-/g, '');
    }
    return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const escapeICS = (text) => {
    if (!text) return '';
    return text
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\n/g, '\\n');
  };

  const uid = `fmn-${Date.now()}-${Math.random().toString(36).substring(2, 11)}@forgetmenot.app`;
  const now = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

  let ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Forget Me Not//Life Admin//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${now}`,
  ];

  if (allDay) {
    ics.push(`DTSTART;VALUE=DATE:${formatDate(startDate, true)}`);
    if (endDate) {
      ics.push(`DTEND;VALUE=DATE:${formatDate(endDate, true)}`);
    }
  } else {
    ics.push(`DTSTART:${formatDate(startDate, false)}`);
    if (endDate) {
      ics.push(`DTEND:${formatDate(endDate, false)}`);
    }
  }

  ics.push(`SUMMARY:${escapeICS(title)}`);
  if (description) ics.push(`DESCRIPTION:${escapeICS(description)}`);
  if (location) ics.push(`LOCATION:${escapeICS(location)}`);

  // Recurrence rule
  if (recurrence) {
    const rruleMap = {
      yearly: 'RRULE:FREQ=YEARLY',
      monthly: 'RRULE:FREQ=MONTHLY',
      weekly: 'RRULE:FREQ=WEEKLY',
      daily: 'RRULE:FREQ=DAILY',
    };
    if (rruleMap[recurrence]) {
      ics.push(rruleMap[recurrence]);
    }
  }

  // Reminder alarm
  if (reminder && reminder > 0) {
    ics.push('BEGIN:VALARM');
    ics.push('ACTION:DISPLAY');
    ics.push(`DESCRIPTION:Reminder: ${title}`);
    ics.push(`TRIGGER:-PT${reminder}M`);
    ics.push('END:VALARM');
  }

  ics.push('END:VEVENT');
  ics.push('END:VCALENDAR');

  return ics.join('\r\n');
};

/**
 * Export event as ICS file (web fallback or manual export)
 */
export const exportEventAsICS = (dateRecord) => {
  const eventData = dateRecordToEventData(dateRecord);
  const icsContent = generateICSContent(eventData);
  
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${eventData.title.replace(/[^a-z0-9]/gi, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  
  toast.success('Calendar file downloaded', {
    description: 'Open the .ics file to manually add to your calendar.',
  });
  
  return { success: true };
};

export default {
  isNativeCalendarAvailable,
  getCalendarPermissionStatus,
  requestCalendarPermission,
  getAvailableCalendars,
  getDefaultCalendar,
  createNativeEvent,
  updateNativeEvent,
  deleteNativeEvent,
  checkEventExists,
  syncDateToCalendar,
  unsyncDateFromCalendar,
  canSyncDateRecord,
  dateRecordToEventData,
  exportEventAsICS,
  CALENDAR_PERMISSION,
  SYNC_STATUS,
};
